import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';

/** Why this tab cannot play: refused at connect, or taken over afterwards. */
export type SessionConflictKind = 'conflict' | 'replaced';

/**
 * Blocking notice shown when this player's session is live in another tab.
 * Plain overlay rather than a MatDialog: it is rendered by the eagerly loaded
 * main layout, which must stay free of heavy dependencies.
 */
@Component({
  selector: 'snk-session-conflict',
  templateUrl: './session-conflict.component.html',
  styleUrl: './session-conflict.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionConflictComponent {
  readonly kind = input.required<SessionConflictKind>();
  /** On the lobby there is nowhere to go back to — show a hint instead. */
  readonly onLobby = input(false);
  /** A takeover is in flight. */
  readonly busy = input(false);

  readonly playHere = output<void>();
  readonly backToLobby = output<void>();

  readonly title = computed(() =>
    this.kind() === 'conflict'
      ? "You're already playing in another tab"
      : 'Session moved to another tab'
  );

  readonly message = computed(() =>
    this.kind() === 'conflict'
      ? 'Only one tab can play at a time. Play here to move your session to this tab — the other one will stop.'
      : 'You started playing in another tab, so this one stopped. Play here to move your session back.'
  );

  private readonly playButton =
    viewChild.required<ElementRef<HTMLButtonElement>>('playButton');

  constructor() {
    // Take focus from the game canvas so the notice is announced and usable
    // from the keyboard.
    afterNextRender(() => this.playButton().nativeElement.focus());
  }
}
